import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import pluralize from 'pluralize';
import classnames from 'classnames';

import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrCalendaryTimeMultiChoice from 'client/components/SrCalendaryTimeMultiChoice';
import SrCalendarDaySlider from 'client/components/SrCalendarDaySlider';
import SrEnablePushNotificationsModal from 'client/components/SrEnablePushNotificationsModal';

import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_PHOTO_DIMENSION_THUMBNAIL,
} from 'common/listingConstants';
import { TOUR_STATUS_REQUESTED, TOUR_STATUS_SCHEDULED } from 'common/tourConstants';
import { SCREEN_ID_LISTING_CHOOSE_TOUR_TIME } from 'client/constants/screenIds';

import {
  getListingUrlPath,
  getTourFromListing,
  getListingManagedPhotoUrls,
  isSelfShowingListing,
  isSunroomListing,
} from 'common/listingHelpers';

import { getAvailableDates } from 'common/tourHelpers';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import { Tour } from 'client/actions';
import ApartmentActions from 'client/actions/Apartment';
import ListingActions from 'client/actions/Listing';
import TourActions from 'client/actions/Tour';
import branch from 'client/core/branch';

import withApiState from 'client/helpers/withApiState';
import { SR_API_ACTION_REQUEST_TOUR_SHOWING, SR_API_ACTION_RESCHEDULE_TOUR } from 'api/actionTypes';

const scheduledTourStatuses = [TOUR_STATUS_REQUESTED, TOUR_STATUS_SCHEDULED];

const SrListingAvailability = ({ listing }) => {
  let availabilityTitle = null;
  let availabilityDescription = null;

  /* eslint-disable max-len */
  if (listing.occupantType === LISTING_OCCUPANT_TYPE_VACANT) {
    availabilityTitle = 'home available now';
    availabilityDescription =
      "The home is currently vacant so it's ready to be toured any time you want!";
  } else if (!listing.occupantType) {
    availabilityTitle = 'home availability';
    availabilityDescription =
      "You'll get a text from Sunroom confirming the home is available for touring!";
  } else {
    availabilityTitle = 'home occupied';
    availabilityDescription =
      'This home is currently occupied by a renter. We need to get approval before showing. Normally 24-hour notice is required.';
  }
  /* eslint-enable max-len */

  if (!availabilityTitle || !availabilityDescription) {
    return null;
  }

  return (
    <div className="section availability">
      <span className="title">{availabilityTitle}</span>
      <span className="description">{availabilityDescription} </span>
    </div>
  );
};

SrListingAvailability.propTypes = {
  listing: PropTypes.object,
};

SrListingAvailability.displayName = 'SrListingAvailability';

@withTransitionHook
@withScreenId(SCREEN_ID_LISTING_CHOOSE_TOUR_TIME)
@withScrollToTop
@withApiState(({ match }) => ({
  requestShowingApiState: {
    action: SR_API_ACTION_REQUEST_TOUR_SHOWING,
    queryId: match.params.listingId,
  },
  rescheduleTourApiState: {
    action: SR_API_ACTION_RESCHEDULE_TOUR,
  },
}))
@branch({
  listing: ['listing'],
  tours: ['tours'],
  screenIdHistory: ['analytics', 'screenIdHistory'],
  availableTourDates: ['availableTourDates'],
  unavailableTourDates: ['unavailableTourDates'],
  addToTourListing: ['addToTourListing'],
  isMobileApp: ['mobileApp', 'isMobileApp'],
  askedPushNotificationPermission: ['mobileApp', 'askedPushNotificationPermission'],
  listingPhotoResolution: ['listingPhotoResolution'],
  pathHistory: ['analytics', 'pathHistory'],
  apartmentsByBuildingId: ['apartments', 'byBuildingId'],
})
class ListingChooseTourTime extends Component {
  static fetchData(match, inject) {
    return ListingActions.getById(inject, parseInt(match.params.listingId, 10)).then((listing) => {
      if (!listing) {
        return;
      }

      const promisesToResolve = [
        TourActions.getAvailableTourDates(inject),
        TourActions.getUnavailableTourDates(inject),
      ];
      if (listing.apartmentBuildingId) {
        promisesToResolve.push(
          ApartmentActions.getByApartmentBuildingId(inject, {
            apartmentBuildingId: listing.apartmentBuildingId,
          }),
        );
      }
      return Promise.all(promisesToResolve);
    });
  }

  static getPageMetadata = () => ({
    pageTitle: 'Schedule Showing | Sunroom Rentals',
    section: 'Schedule Showing',
    // canonical: '/',
    keywords: 'some, keywords',
    description: 'Site description here.',
  });

  static propTypes = {
    listing: PropTypes.object,
    availableTourDates: PropTypes.array,
    requestShowingApiState: PropTypes.object.isRequired,
    rescheduleTourApiState: PropTypes.object.isRequired,
    isTransitioning: PropTypes.bool,
    addToTourListing: PropTypes.object,
    isMobileApp: PropTypes.bool.isRequired,
    askedPushNotificationPermission: PropTypes.bool.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
    apartmentsByBuildingId: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    const locationQuery = queryString.parse(props.location.search);

    this.state = {
      selectedTimes: [],
      selectedDate: locationQuery.date,
      createNewTour: locationQuery.createNewTour === 'true',
      tourId: props.match.params.tourId ? parseInt(props.match.params.tourId, 10) : null,
      isReschedule: props.location.pathname.includes('reschedule'),
      listingToAddId: locationQuery.listingToAddId
        ? parseInt(locationQuery.listingToAddId, 10)
        : null,
      shouldAskPushNotificationPermission:
        props.isMobileApp && !props.askedPushNotificationPermission,
      showEnablePushNotificationModal: false,
      isRequestShowingStepSubmitting: false,
    };

    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidMount() {
    this._checkAvailability();
    this._checkAvailabilityInterval = global.setInterval(this._checkAvailability, 60000);
  }

  componentWillUnmount() {
    global.clearInterval(this._checkAvailabilityInterval);
  }

  _checkAvailability = () => {
    const { listing, availableTourDates, unavailableTourDates } = this.props;
    const { selectedDate } = this.state;
    if (listing && availableTourDates) {
      const availableDates = getAvailableDates({
        availableTourDates: availableTourDates || [],
        unavailableTourDates: unavailableTourDates || [],
        isSunroomListing: isSunroomListing(listing),
      })
        .filter(({ disabled }) => !disabled)
        .map(({ date }) => date);
      if (selectedDate && !availableDates.includes(selectedDate)) {
        this._onSelectedDate(availableDates[0]);
      }
    }
  };

  _onSelectedDate = (selectedDate) => {
    this.setState({
      selectedDate,
      selectedTimes: [],
    });
  };

  async onSubmit() {
    const { listing, dispatch } = this.props;

    const {
      selectedDate,
      selectedTimes,
      tourId,
      createNewTour,
      listingToAddId,
      shouldAskPushNotificationPermission,
    } = this.state;

    const locationQuery = queryString.parse(location.search);
    const requestedFrom = locationQuery.requestedFrom ? locationQuery.requestedFrom : null;

    if (shouldAskPushNotificationPermission) {
      this.setState({
        showEnablePushNotificationModal: true,
      });
    } else if (tourId) {
      if (listingToAddId) {
        const requestShowingPayload = {
          listingId: listingToAddId,
          locationQuery: {
            requestedFrom,
            date: selectedDate,
            addToTourId: tourId,
          },
        };
        this.setState({ isRequestShowingStepSubmitting: true });
        dispatch(TourActions.requestShowingStepSubmit, requestShowingPayload);
      }
      dispatch(Tour.rescheduleTour, {
        tourId: tourId,
        startDate: selectedDate,
        availableTimes: selectedTimes,
        shouldSendRequestShowingAutoText: false,
      });
    } else {
      this.setState({ isRequestShowingStepSubmitting: true });
      dispatch(Tour.requestShowingStepSubmit, {
        listingId: listing.id,
        locationQuery: {
          requestedFrom,
          date: selectedDate,
          times: selectedTimes,
          createNewTour: createNewTour,
        },
      });
    }
  }

  _onCloseEnablePushNotificationsModal = () => {
    this.setState(
      {
        shouldAskPushNotificationPermission: false,
        showEnablePushNotificationModal: false,
      },
      this.onSubmit,
    );
  };

  render() {
    const {
      listing,
      requestShowingApiState,
      rescheduleTourApiState,
      tours,
      isTransitioning,
      addToTourListing,
      listingPhotoResolution,
      apartmentsByBuildingId,
    } = this.props;

    const {
      selectedTimes,
      selectedDate,
      tourId,
      isReschedule,
      showEnablePushNotificationModal,
      isRequestShowingStepSubmitting,
    } = this.state;

    if (isTransitioning) {
      return null;
    }

    if (!listing) {
      return <Redirect to="/feed" />;
    }
    if (isSelfShowingListing(listing)) {
      const apartment = apartmentsByBuildingId[listing.apartmentBuildingId];
      const redirectToUrl =
        apartment && apartment.selfTourFlowEnabled
          ? `/listing/${listing.id}/self-tour`
          : `/listing/${listing.id}?showSelfShowingSteps=true`;

      return <Redirect to={redirectToUrl} />;
    }

    const listingIsScheduled =
      tours &&
      tours
        .filter(({ status }) => scheduledTourStatuses.includes(status))
        .some(({ listings }) => {
          return listings.some(({ id }) => listing.id === id);
        });

    if (listingIsScheduled && !tourId && !isRequestShowingStepSubmitting) {
      return <Redirect to={getListingUrlPath(listing)} />;
    }

    const tourMatch = getTourFromListing({
      listingId: listing.id,
      tours: tours,
    });

    const tourMatchListings = tourMatch && tourMatch.listings.slice();
    if (addToTourListing && tourMatchListings) {
      tourMatchListings.push(addToTourListing);
    }
    const managedPhotoUrls = getListingManagedPhotoUrls({ listing });
    const moreListings = tourMatchListings && tourMatchListings.length > 3;
    const isApiStateInProgress =
      requestShowingApiState.inProgress || rescheduleTourApiState.inProgress;

    return (
      <div className="page-scheduleTourTime">
        <Navbar
          leftButton={
            <SrLink
              className="link back"
              restoreScrollPosition
              redirectToLastLocation
              to={`/listing/${listing.id}`}
            />
          }
        />
        <div className="header" style={{ backgroundImage: `url("${managedPhotoUrls[0]}")` }}>
          <span className="headline">
            {isReschedule ? 'Reschedule Tour' : 'Choose Appointment Time'}
          </span>
          {tourMatch && (
            <div>
              <span className="subhead">
                Seeing {tourMatchListings.length} {pluralize('rental', tourMatchListings.length)} on
                this tour
              </span>
              <div className="listingImageWrapper">
                {tourMatchListings.slice(0, 3).map((listing) => {
                  const listingManagedPhotoUrls = getListingManagedPhotoUrls({
                    listing: listing,
                    dimension: LISTING_PHOTO_DIMENSION_THUMBNAIL,
                    resolution: listingPhotoResolution,
                  });
                  return (
                    <div
                      className="listingImage"
                      key={listing.id}
                      style={{ backgroundImage: `url("${listingManagedPhotoUrls[0]}")` }}
                    />
                  );
                })}
                {moreListings && (
                  <div className="listingImage more">+ {tourMatchListings.length - 3}</div>
                )}
              </div>
            </div>
          )}
        </div>
        <SrListingAvailability listing={listing} />
        <div className="section day">
          <span className="title">choose day</span>
          <SrCalendarDaySlider
            onChange={this._onSelectedDate}
            selectedDate={selectedDate}
            isSunroomListing={isSunroomListing(listing)}
          />
        </div>
        <div className={classnames('section timeSelection')}>
          <span className="title">choose time (multiple preferred)</span>
          {selectedDate && (
            <SrCalendaryTimeMultiChoice
              selectedDate={selectedDate}
              onChange={(selectedTimes) => {
                this.setState({ selectedTimes });
              }}
              isSunroomListing={isSunroomListing(listing)}
            />
          )}
          <div className="actions">
            <button
              className="button yellow"
              type="button"
              disabled={
                !selectedTimes.length || isApiStateInProgress || isRequestShowingStepSubmitting
              }
              onClick={this.onSubmit}
            >
              {isApiStateInProgress ? 'requesting...' : "let's go!"}
            </button>
          </div>
        </div>

        {showEnablePushNotificationModal && (
          <SrEnablePushNotificationsModal
            isTourSection
            onClose={this._onCloseEnablePushNotificationsModal}
          />
        )}
      </div>
    );
  }
}

ListingChooseTourTime.displayName = 'ListingChooseTourTime';

export default ListingChooseTourTime;
